import { withoutTrailingSlash } from 'ufo'
import type { Ref } from 'vue'

import { breadcrumbJsonLd, jsonLdScript } from '../utils/jsonLd'

import type { CategoryDto } from '#Products/models/Pim/CategoryDto'
import { categoryAncestorChain } from '#Products/support/categoryAncestorChain'

/**
 * SEO meta for category / home (SSR): title/description, Open Graph, and
 * JSON-LD BreadcrumbList aligned with visible crumbs (Home → Catalog → … → current).
 * Admin meta (MR 9b) overrides auto title/description/og — see SeoService.
 */
export const useCategorySeo = (
  category: Ref<CategoryDto | undefined>,
  categoriesByIds?: Ref<Record<string, CategoryDto> | undefined>,
) => {
  const { service: seo } = useSeo()
  const { routing } = useProducts()
  const url = useRequestURL()
  // Capture Nuxt instance in setup (context present here); the og-image
  // watchEffect re-runs after setup flushes and loses the active context —
  // `seo.categoryOgImage` → `useImage()` → `useRuntimeConfig()` then throws
  // "composable outside setup". Re-establish context with `runWithContext`.
  const nuxtApp = useNuxtApp()
  // Paginated listing (?page=N): "— страница N" keeps title/description unique.
  const { page } = useListingPagination()

  const pageTitle = () => {
    const title = seo.categoryTitle(category.value)

    return page.value > 1
      ? `${title ?? 'Каталог'} — страница ${page.value}`
      : title
  }

  const pageDescription = () => {
    const description = seo.categoryDescription(category.value)

    return page.value > 1
      ? `${description} Страница ${page.value}.`
      : description
  }

  // Eager og + JSON-LD in setup (see useProductDetailsSeo).
  const ogImage = ref<string | undefined>()
  watchEffect(() => {
    ogImage.value = nuxtApp.runWithContext(() =>
      seo.categoryOgImage(category.value),
    )
  })

  const jsonLdScripts = ref<ReturnType<typeof jsonLdScript>[]>([])
  watchEffect(() => {
    if (!category.value) {
      jsonLdScripts.value = []
      return
    }

    const byIds = categoriesByIds?.value ?? {}
    const ancestors = categoryAncestorChain(category.value, byIds)
    const chain = [...ancestors, category.value]
    const catalogUrl = withoutTrailingSlash(
      `${url.origin}${routing.getCatalog().route}`,
    )

    jsonLdScripts.value = [
      jsonLdScript(breadcrumbJsonLd([
        { name: 'Главная', url: `${url.origin}/` },
        { name: 'Каталог', url: catalogUrl },
        ...chain.map((item, index) => ({
          name: item.name,
          // Last crumb is the current page — omit URL.
          url: index < chain.length - 1
            ? withoutTrailingSlash(`${url.origin}${routing.getCatalog(item).route}`)
            : undefined,
        })),
      ])),
    ]
  })

  useSeoMeta({
    title: pageTitle,
    description: pageDescription,

    ogTitle: pageTitle,
    ogDescription: pageDescription,
    // Category og from admin; else storefront default (same as seoRoot plugin).
    ogImage: () => ogImage.value,
  })

  useHead(computed(() => ({ script: jsonLdScripts.value })))
}
